"use client";

// packages/react/collapsible/src/Collapsible.tsx
import _defineProperty from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _slicedToArray from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
var _excluded = ["__scopeCollapsible", "open", "defaultOpen", "disabled", "onOpenChange"],
  _excluded2 = ["__scopeCollapsible"],
  _excluded3 = ["forceMount"],
  _excluded4 = ["__scopeCollapsible", "present", "children"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import * as React from "react";
import { composeEventHandlers } from "@radix-ui/primitive";
import { createContextScope } from "@radix-ui/react-context";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { useLayoutEffect } from "@radix-ui/react-use-layout-effect";
import { useComposedRefs } from "@radix-ui/react-compose-refs";
import { Primitive } from "@radix-ui/react-primitive";
import { Presence } from "@radix-ui/react-presence";
import { useId } from "@radix-ui/react-id";
import { jsx } from "react/jsx-runtime";
var COLLAPSIBLE_NAME = "Collapsible";
var _createContextScope = createContextScope(COLLAPSIBLE_NAME),
  _createContextScope2 = _slicedToArray(_createContextScope, 2),
  createCollapsibleContext = _createContextScope2[0],
  createCollapsibleScope = _createContextScope2[1];
var _createCollapsibleCon = createCollapsibleContext(COLLAPSIBLE_NAME),
  _createCollapsibleCon2 = _slicedToArray(_createCollapsibleCon, 2),
  CollapsibleProvider = _createCollapsibleCon2[0],
  useCollapsibleContext = _createCollapsibleCon2[1];
var Collapsible = React.forwardRef(function (props, forwardedRef) {
  var __scopeCollapsible = props.__scopeCollapsible,
    openProp = props.open,
    defaultOpen = props.defaultOpen,
    disabled = props.disabled,
    onOpenChange = props.onOpenChange,
    collapsibleProps = _objectWithoutProperties(props, _excluded);
  var _useControllableState = useControllableState({
      prop: openProp,
      defaultProp: defaultOpen,
      onChange: onOpenChange
    }),
    _useControllableState2 = _slicedToArray(_useControllableState, 2),
    _useControllableState3 = _useControllableState2[0],
    open = _useControllableState3 === void 0 ? false : _useControllableState3,
    setOpen = _useControllableState2[1];
  return /* @__PURE__ */jsx(CollapsibleProvider, {
    scope: __scopeCollapsible,
    disabled: disabled,
    contentId: useId(),
    open: open,
    onOpenToggle: React.useCallback(function () {
      return setOpen(function (prevOpen) {
        return !prevOpen;
      });
    }, [setOpen]),
    children: /* @__PURE__ */jsx(Primitive.div, _objectSpread(_objectSpread({
      "data-state": getState(open),
      "data-disabled": disabled ? "" : void 0
    }, collapsibleProps), {}, {
      ref: forwardedRef
    }))
  });
});
Collapsible.displayName = COLLAPSIBLE_NAME;
var TRIGGER_NAME = "CollapsibleTrigger";
var CollapsibleTrigger = React.forwardRef(function (props, forwardedRef) {
  var __scopeCollapsible = props.__scopeCollapsible,
    triggerProps = _objectWithoutProperties(props, _excluded2);
  var context = useCollapsibleContext(TRIGGER_NAME, __scopeCollapsible);
  return /* @__PURE__ */jsx(Primitive.button, _objectSpread(_objectSpread({
    type: "button",
    "aria-controls": context.contentId,
    "aria-expanded": context.open || false,
    "data-state": getState(context.open),
    "data-disabled": context.disabled ? "" : void 0,
    disabled: context.disabled
  }, triggerProps), {}, {
    ref: forwardedRef,
    onClick: composeEventHandlers(props.onClick, context.onOpenToggle)
  }));
});
CollapsibleTrigger.displayName = TRIGGER_NAME;
var CONTENT_NAME = "CollapsibleContent";
var CollapsibleContent = React.forwardRef(function (props, forwardedRef) {
  var forceMount = props.forceMount,
    contentProps = _objectWithoutProperties(props, _excluded3);
  var context = useCollapsibleContext(CONTENT_NAME, props.__scopeCollapsible);
  return /* @__PURE__ */jsx(Presence, {
    present: forceMount || context.open,
    children: function children(_ref) {
      var present = _ref.present;
      return /* @__PURE__ */jsx(CollapsibleContentImpl, _objectSpread(_objectSpread({}, contentProps), {}, {
        ref: forwardedRef,
        present: present
      }));
    }
  });
});
CollapsibleContent.displayName = CONTENT_NAME;
var CollapsibleContentImpl = React.forwardRef(function (props, forwardedRef) {
  var _objectSpread2;
  var __scopeCollapsible = props.__scopeCollapsible,
    present = props.present,
    children = props.children,
    contentProps = _objectWithoutProperties(props, _excluded4);
  var context = useCollapsibleContext(CONTENT_NAME, __scopeCollapsible);
  var _React$useState = React.useState(present),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    isPresent = _React$useState2[0],
    setIsPresent = _React$useState2[1];
  var ref = React.useRef(null);
  var composedRefs = useComposedRefs(forwardedRef, ref);
  var heightRef = React.useRef(0);
  var height = heightRef.current;
  var widthRef = React.useRef(0);
  var width = widthRef.current;
  var isOpen = context.open || isPresent;
  var isMountAnimationPreventedRef = React.useRef(isOpen);
  var originalStylesRef = React.useRef();
  React.useEffect(function () {
    var rAF = requestAnimationFrame(function () {
      return isMountAnimationPreventedRef.current = false;
    });
    return function () {
      return cancelAnimationFrame(rAF);
    };
  }, []);
  useLayoutEffect(function () {
    var node = ref.current;
    if (node) {
      originalStylesRef.current = originalStylesRef.current || {
        transitionDuration: node.style.transitionDuration,
        animationName: node.style.animationName
      };
      node.style.transitionDuration = "0s";
      node.style.animationName = "none";
      var rect = node.getBoundingClientRect();
      heightRef.current = rect.height;
      widthRef.current = rect.width;
      if (!isMountAnimationPreventedRef.current) {
        node.style.transitionDuration = originalStylesRef.current.transitionDuration;
        node.style.animationName = originalStylesRef.current.animationName;
      }
      setIsPresent(present);
    }
  }, [context.open, present]);
  return /* @__PURE__ */jsx(Primitive.div, _objectSpread(_objectSpread({
    "data-state": getState(context.open),
    "data-disabled": context.disabled ? "" : void 0,
    id: context.contentId,
    hidden: !isOpen
  }, contentProps), {}, {
    ref: composedRefs,
    style: _objectSpread((_objectSpread2 = {}, _defineProperty(_objectSpread2, "--radix-collapsible-content-height", height ? "".concat(height, "px") : void 0), _defineProperty(_objectSpread2, "--radix-collapsible-content-width", width ? "".concat(width, "px") : void 0), _objectSpread2), props.style),
    children: isOpen && children
  }));
});
function getState(open) {
  return open ? "open" : "closed";
}
var Root = Collapsible;
var Trigger = CollapsibleTrigger;
var Content = CollapsibleContent;
export { Collapsible, CollapsibleContent, CollapsibleTrigger, Content, Root, Trigger, createCollapsibleScope };